import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { Keyboard } from "@capacitor/keyboard";
import { StatusBar, Style } from "@capacitor/status-bar";
import { LiveUpdate as CapacitorLiveUpdate } from "@capawesome/capacitor-live-update";
import { getMinSupportedNativePolicy } from "../native-version-guard";
import { captureException, captureMessage, SentryReact } from "../sentry";
import { init as initBadge } from "./badge/index";
import {
  getStartupLiveUpdateChannel,
  LIVE_UPDATE_JUST_RELOADED_KEY,
} from "./live-update-channel";
import { getMobileVersionTelemetry } from "./mobile-version";

const LIVE_UPDATE_READY_TIMEOUT_MS = 10000;
const LIVE_UPDATE_SYNC_TIMEOUT_MS = 30000;
const LIVE_UPDATE_RELOAD_TIMEOUT_MS = 15000;
const LIVE_UPDATE_CHANNEL_TIMEOUT_MS = 5000;
const FORCE_LOCAL_BUNDLE_ENV_KEY = "VITE_FORCE_LOCAL_BUNDLE";

// Debug mode to force local APK bundle and ignore OTA updates.
// Enable via env (VITE_FORCE_LOCAL_BUNDLE=1) or by adding
// ?force_local_bundle=1 to the URL.
function shouldForceLocalBundle() {
  try {
    const viteEnv = import.meta.env as Record<string, string | undefined>;
    const envFlagRaw = viteEnv[FORCE_LOCAL_BUNDLE_ENV_KEY];
    const fromEnv = envFlagRaw === "1" || envFlagRaw?.toLowerCase() === "true";
    if (fromEnv) {
      return true;
    }

    const searchParams = new URLSearchParams(window.location.search);
    const fromQuery = searchParams.get("force_local_bundle") === "1";

    // Legacy cleanup: older builds used localStorage("FORCE_LOCAL_BUNDLE") as a
    // sticky switch. We ignore it now to avoid accidental "always local" runs.
    try {
      if (window.localStorage.getItem("FORCE_LOCAL_BUNDLE") === "1") {
        window.localStorage.removeItem("FORCE_LOCAL_BUNDLE");
        console.info(
          "🧹 Ignored and cleared legacy FORCE_LOCAL_BUNDLE localStorage flag."
        );
      }
    } catch {
      // localStorage may be unavailable
    }

    return fromQuery;
  } catch {
    return false;
  }
}

function compareVersions(v1: string, v2: string): number {
  const parts1 = v1.split(".").map((p) => parseInt(p, 10));
  const parts2 = v2.split(".").map((p) => parseInt(p, 10));

  for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
    const p1 = parts1[i] || 0;
    const p2 = parts2[i] || 0;
    if (p1 > p2) return 1;
    if (p1 < p2) return -1;
  }
  return 0;
}

function isNativeVersionSupported() {
  const platform = Capacitor.getPlatform();
  if (platform !== "ios" && platform !== "android") {
    return true;
  }

  const version = window.appInfos?.version;
  const build = window.appInfos?.build;
  if (!version || !build) {
    return true;
  }

  const viteEnv = import.meta.env as Record<string, string | undefined>;
  const minSupportedNativePolicy = getMinSupportedNativePolicy(
    platform,
    viteEnv
  );
  const { minBuild, minVersion } = minSupportedNativePolicy;

  window.nativeVersionGuard = {
    currentBuild: build,
    currentVersion: version,
    minBuild,
    minVersion,
    platform,
  };

  const versionComparison = compareVersions(version, minVersion);
  if (versionComparison < 0) {
    return false;
  }
  if (versionComparison === 0) {
    return parseInt(build, 10) >= minBuild;
  }
  return true;
}

async function withTimeout<T>(
  promise: Promise<T>,
  ms: number,
  label: string
): Promise<T> {
  let timeoutHandle: number | undefined;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutHandle = window.setTimeout(() => {
      const msg = `⏱️ Timeout ${label} after ${ms}ms`;
      console.warn(msg);
      captureException(new Error(msg), { extra: { label, ms } });
      reject(new Error(msg));
    }, ms);
  });
  // eslint-disable-next-line no-useless-catch
  try {
    const result = await Promise.race([promise, timeoutPromise]);
    if (timeoutHandle) window.clearTimeout(timeoutHandle);
    return result as T;
  } catch (err) {
    throw err;
  }
}

// Init for every environment, including "web"
async function commonInit() {
  window.appInfos = undefined;
  window.nativeVersionGuard = undefined;
  setMobileVersionTelemetryInSentry();
  return;
}

function setMobileVersionTelemetryInSentry() {
  const mobileVersionTelemetry = getMobileVersionTelemetry(window.appInfos);

  SentryReact.setTag("mobile_app_id", mobileVersionTelemetry.mobile_app_id);
  SentryReact.setTag("mobile_app_name", mobileVersionTelemetry.mobile_app_name);
  SentryReact.setTag(
    "mobile_build_number",
    mobileVersionTelemetry.mobile_build_number
  );
  SentryReact.setTag("mobile_platform", mobileVersionTelemetry.mobile_platform);
  SentryReact.setTag("mobile_version", mobileVersionTelemetry.mobile_version);
  SentryReact.setTag(
    "mobile_version_name",
    mobileVersionTelemetry.mobile_version_name
  );
  SentryReact.setTag(
    "mobile_is_native",
    String(mobileVersionTelemetry.mobile_is_native)
  );
  SentryReact.setTag(
    "mobile_version_source",
    mobileVersionTelemetry.mobile_version_source
  );
  SentryReact.setContext("mobile_app", mobileVersionTelemetry);
}

// Init for native platforms only (ios | android)
async function nativeInit() {
  if (Capacitor.isPluginAvailable("StatusBar")) {
    await StatusBar.setStyle({ style: Style.Light });
  }
  if (Capacitor.isPluginAvailable("App")) {
    try {
      window.appInfos = await App.getInfo();
    } catch (error) {
      window.appInfos = undefined;
      captureMessage("Unable to get native app version from Capacitor App", {
        extra: { error },
      });
    }
  }
  setMobileVersionTelemetryInSentry();

  const isBelowMinimumNativeVersion = !isNativeVersionSupported();

  if (isBelowMinimumNativeVersion) {
    const nativeVersionGuard = window.nativeVersionGuard;
    const message = `🚫 Native app version ${nativeVersionGuard?.currentVersion} (${nativeVersionGuard?.currentBuild}) is below minimum ${nativeVersionGuard?.minVersion} (${nativeVersionGuard?.minBuild}) on ${nativeVersionGuard?.platform}. Attempting LiveUpdate recovery before forcing store update.`;
    console.warn(message);
    captureMessage(message, {
      level: "warning",
    });
  }

  await initBadge();

  // Capawesome LiveUpdate is used to update the app from the server url fro Capacitor app
  if (Capacitor.isPluginAvailable("LiveUpdate")) {
    let result: Awaited<ReturnType<typeof CapacitorLiveUpdate.ready>> | null =
      null;
    try {
      result = await withTimeout(
        CapacitorLiveUpdate.ready(),
        LIVE_UPDATE_READY_TIMEOUT_MS,
        "LiveUpdate.ready"
      );
    } catch (e) {
      console.warn("⚠️ LiveUpdate.ready timeout o error", e);
      captureMessage("⚠️ LiveUpdate.ready fallo/timeout", {
        extra: { error: (e as Error).message },
      });
    }
    if (result?.currentBundleId) {
      const message = `🔷 The Capacitor app is now using the bundle with the identifier ${result.currentBundleId}.`;
      console.info(message);
      captureMessage(message);
    }
    if (result?.previousBundleId) {
      const message = `🔷 The Capacitor app was using the bundle with the identifier ${result.previousBundleId}.`;
      console.info(message);
      captureMessage(message);
    }
    if (result?.rollback) {
      const message = "🔷 The Capacitor app was reset to the default bundle.";
      console.info(message);
      captureMessage(message);
    }

    // Keep a selected staging PR across launches; production always uses prod.
    if (window.appInfos?.id) {
      try {
        const currentChannel = await withTimeout(
          CapacitorLiveUpdate.getChannel(),
          LIVE_UPDATE_CHANNEL_TIMEOUT_MS,
          "LiveUpdate.getChannel"
        );
        const expectedChannel = getStartupLiveUpdateChannel(
          window.appInfos.id,
          currentChannel.channel
        );
        if (currentChannel.channel !== expectedChannel) {
          await withTimeout(
            CapacitorLiveUpdate.setChannel({ channel: expectedChannel }),
            LIVE_UPDATE_CHANNEL_TIMEOUT_MS,
            "LiveUpdate.setChannel"
          );
          const previousChannel = currentChannel.channel ?? "<empty>";
          const message = `🔷 LiveUpdate channel set to "${expectedChannel}" (previous: "${previousChannel}") for app id "${window.appInfos?.id}".`;
          console.info(message);
          captureMessage(message);
        }
      } catch (e) {
        console.warn("⚠️ LiveUpdate.get/setChannel timeout o error", e);
        captureMessage("⚠️ LiveUpdate.get/setChannel fallo/timeout", {
          extra: { error: (e as Error).message },
        });
      }
    }

    if (shouldForceLocalBundle()) {
      const message =
        "🧪 FORCE_LOCAL_BUNDLE=1 detected: skipping LiveUpdate sync/reload and resetting to default bundle.";
      console.info(message);
      captureMessage(message);
      try {
        await withTimeout(
          CapacitorLiveUpdate.reset(),
          10000,
          "LiveUpdate.reset"
        );
      } catch (e) {
        console.warn("⚠️ LiveUpdate.reset timeout o error", e);
        captureMessage("⚠️ LiveUpdate.reset fallo/timeout", {
          extra: { error: (e as Error).message },
        });
      }
      if (isBelowMinimumNativeVersion) {
        const nativeVersionGuard = window.nativeVersionGuard;
        const blockedMessage = `🚫 Native app version ${nativeVersionGuard?.currentVersion} (${nativeVersionGuard?.currentBuild}) remains below minimum ${nativeVersionGuard?.minVersion} (${nativeVersionGuard?.minBuild}) on ${nativeVersionGuard?.platform}. Blocking app usage until store update.`;
        console.warn(blockedMessage);
        captureMessage(blockedMessage, {
          level: "warning",
        });
      }
      return;
    }

    // const listener = await CapacitorLiveUpdate.addListener(
    //   "downloadBundleProgress",
    //   (downloadEvent) => {
    //     console.info("🔷 Capawesome LiveUpdate downloadEvent", downloadEvent);
    //     console.info("🔷 downloadEvent.progress", downloadEvent.progress);
    //   }
    // );

    // Add sync functionality to check for updates
    const sync = async () => {
      try {
        const syncResult = await withTimeout(
          CapacitorLiveUpdate.sync(),
          LIVE_UPDATE_SYNC_TIMEOUT_MS,
          "LiveUpdate.sync"
        );
        console.info("🔷 syncResult", syncResult);
        if (syncResult.nextBundleId) {
          const message = `🔷 New bundle available: ${syncResult.nextBundleId}`;
          console.info(message);
          captureMessage(message);
          // Don't reload here - let the caller decide
          return syncResult; // Return the result
        }
        return syncResult;
      } catch (error) {
        const message = "🔷 Live Update sync failed";
        console.info(message, { extra: { error } });
        captureMessage(message, { extra: { error } });
        return null;
      }
    };

    // Perform initial sync when app starts
    let syncResult: Awaited<ReturnType<typeof sync>> | null = null;
    syncResult = await sync();

    if (syncResult?.nextBundleId) {
      try {
        // Signal to the auth flow that a LiveUpdate reload is about to happen.
        // After reload the webview restarts from scratch; the auth flow will
        // read this flag and apply an extended delay before opening the browser.
        try {
          window.localStorage.setItem(
            LIVE_UPDATE_JUST_RELOADED_KEY,
            Date.now().toString()
          );
        } catch {
          // localStorage may be unavailable; ignore
        }
        await withTimeout(
          CapacitorLiveUpdate.reload(),
          LIVE_UPDATE_RELOAD_TIMEOUT_MS,
          "LiveUpdate.reload"
        );
      } catch (e) {
        console.warn("LiveUpdate.reload timeout o error", e);
        captureMessage("LiveUpdate.reload fallo/timeout", {
          extra: { error: (e as Error).message },
        });
      }
    }
    // void listener.remove();
  }

  if (isBelowMinimumNativeVersion) {
    const nativeVersionGuard = window.nativeVersionGuard;
    const message = `🚫 Native app version ${nativeVersionGuard?.currentVersion} (${nativeVersionGuard?.currentBuild}) remains below minimum ${nativeVersionGuard?.minVersion} (${nativeVersionGuard?.minBuild}) on ${nativeVersionGuard?.platform}. Blocking app usage until store update.`;
    console.warn(message);
    captureMessage(message, {
      level: "warning",
    });
    return;
  }
}

// Android specific init
async function androidInit() {
  return;
}

// Ios specific init
async function iOSInit() {
  if (Capacitor.isPluginAvailable("Keyboard")) {
    await Keyboard.setAccessoryBarVisible({ isVisible: true });
  }
}

async function platformSpecificInit() {
  const platform = Capacitor.getPlatform();
  const isNative = Capacitor.isNativePlatform();

  if (isNative) {
    await nativeInit();
  }
  if (platform === "ios") {
    await iOSInit();
  } else if (platform === "android") {
    await androidInit();
  }
}

async function initCapacitor() {
  await commonInit();
  await platformSpecificInit();
  console.info("Clovis capacitor config has been set");
  return null;
}

export { initCapacitor, LIVE_UPDATE_JUST_RELOADED_KEY };
